import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link as RouterLink} from '@github-ui/react-router'
import type {IssueStateReason} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import {IssueClosedIcon, IssueOpenedIcon, SkipIcon} from '@primer/octicons-react'
import {Link as PrimerLink, Token} from '@primer/react'
import type {FC} from 'react'

import {parseGitHubIssueUrl} from '../../utils/urls'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import styles from './TrackedBy.module.css'

type TrackingIssue = {
  number: number
  url: string
  stateReason?: IssueStateReason | null
}

export type TrackedByFragment = Pick<IssueSecondaryNode, 'trackedInIssues'>

type TrackedByProps = {
  trackedByData?: TrackedByFragment
  url: string
  isSmall?: boolean
}

export const SmallTrackedBy: FC<TrackedByProps> = props => <TrackedBy {...props} isSmall />

export function TrackedBy({trackedByData: data, url, isSmall = false}: TrackedByProps) {
  if (!data) {
    return null
  }

  const numberOfTrackingIssues = data.trackedInIssues?.totalCount ?? 0
  if (numberOfTrackingIssues === 0) return null

  const trackingIssues = data.trackedInIssues?.nodes?.flatMap(a => (a ? a : [])) ?? []

  const [firstTrackingIssue] = trackingIssues
  const trackingItem =
    trackingIssues.length === 1 && firstTrackingIssue ? (
      <SingleTrackingItem trackingIssue={firstTrackingIssue} url={url} omitIcon={isSmall} omitHover={isSmall} />
    ) : (
      <MultipleTrackingItems trackingIssues={trackingIssues} url={url} omitHover={isSmall} />
    )

  const tokenContent = <div className={styles.trackingItemContainer}>{trackingItem}</div>

  if (isSmall) {
    return tokenContent
  }

  return (
    <div>
      <Token text={tokenContent} size={'xlarge'} className={styles.trackingToken} />
    </div>
  )
}

function getIssueIcon(stateReason?: IssueStateReason | null) {
  if (stateReason === 'COMPLETED') {
    return {
      colorClass: 'iconDone' as const,
      icon: IssueClosedIcon,
    }
  } else if (stateReason === 'NOT_PLANNED') {
    return {
      colorClass: 'iconMuted' as const,
      icon: SkipIcon,
    }
  } else {
    return {
      colorClass: 'iconOpen' as const,
      icon: IssueOpenedIcon,
    }
  }
}

type TrackingItemBaseProps = {
  url: string
  omitIcon?: boolean
  omitHover?: boolean
}
type SingleTrackingItemProps = {
  trackingIssue: TrackingIssue
} & TrackingItemBaseProps

const SingleTrackingItem: FC<SingleTrackingItemProps> = ({trackingIssue, url, omitIcon, omitHover}) => {
  const icon = getIssueIcon(trackingIssue.stateReason)
  const IconComponent = icon.icon

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const trackedInHovercardAttrs = omitHover
    ? {}
    : getHovercardAttributesForType('tracked_in', {url: `${url}/tracked_in/hovercard/`})
  const issueHovercardAttrs = omitHover
    ? {}
    : getHovercardAttributesForType('issue', {url: `${trackingIssue.url}/hovercard`})
  const currentIssue = parseGitHubIssueUrl(url)
  const trackedIssue = parseGitHubIssueUrl(trackingIssue.url)
  const linkUrl =
    currentIssue && trackedIssue && currentIssue.owner === trackedIssue.owner && currentIssue.repo === trackedIssue.repo
      ? `/${trackedIssue.owner}/${trackedIssue.repo}/issues/${trackedIssue.number}`
      : trackingIssue.url

  return (
    <>
      <span {...trackedInHovercardAttrs} className={styles.trackedByText}>
        Listed in
      </span>
      <div key={trackingIssue.number} {...issueHovercardAttrs} className={styles.trackingInfoContainer}>
        {!omitIcon && <IconComponent size={16} className={styles[icon.colorClass]} />}
        <PrimerLink as={RouterLink} to={linkUrl}>{`#${trackingIssue.number}`}</PrimerLink>
      </div>
    </>
  )
}

type MultipleTrackingItemProps = {
  trackingIssues: TrackingIssue[]
} & TrackingItemBaseProps

const MultipleTrackingItems: FC<MultipleTrackingItemProps> = ({trackingIssues, url, omitHover}) => {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = omitHover
    ? {}
    : getHovercardAttributesForType('tracked_in', {url: `${url}/tracked_in/hovercard/`})

  return <span {...hovercardAttrs}>{`Listed in ${trackingIssues.length} issues`}</span>
}
